import { useState } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Lock, CheckCircle2, XCircle, Clock, Trophy } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { Countdown } from "@/components/Countdown";
import { SocialLinks } from "@/components/SocialLinks";
import { FaTelegram } from "react-icons/fa6";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TYPE_LABEL = {
  marcatore: "Marcatore",
  risultato: "Risultato esatto",
  combinato: "Combinato",
  "1x2": "Esito 1X2",
  goalnogoal: "Goal / No Goal",
};

const SCHEDINA_OPTS = {
  "1x2": [{ v: "1", l: "1" }, { v: "X", l: "X" }, { v: "2", l: "2" }],
  goalnogoal: [{ v: "goal", l: "Goal" }, { v: "nogoal", l: "No Goal" }],
};

function Stepper({ label, value, setValue, disabled, testId }) {
  const num = value === "" ? 0 : parseInt(value, 10);
  const dec = () => setValue(String(Math.max(0, num - 1)));
  const inc = () => setValue(String(num + 1));
  return (
    <div className="text-center">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-zinc-500">{label}</p>
      <div className="flex items-center gap-1">
        <button type="button" onClick={dec} disabled={disabled} data-testid={`${testId}-minus`}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-950 text-lg font-black text-green-400 disabled:opacity-40">−</button>
        <Input data-testid={testId} type="number" inputMode="numeric" min={0} disabled={disabled} value={value}
          onChange={(e) => setValue(e.target.value)} className="h-11 w-14 rounded-xl text-center font-mono-t text-xl font-black" />
        <button type="button" onClick={inc} disabled={disabled} data-testid={`${testId}-plus`}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500 text-lg font-black text-zinc-950 disabled:opacity-40">+</button>
      </div>
    </div>
  );
}

function ScoreInputs({ home, away, setHome, setAway, disabled, predId }) {
  return (
    <div className="flex items-center justify-center gap-3 rounded-2xl bg-slate-100 p-4">
      <Stepper label="Casa" value={home} setValue={setHome} disabled={disabled} testId={`quiz-home-score-${predId}`} />
      <span className="font-display text-3xl font-black text-zinc-400">-</span>
      <Stepper label="Ospite" value={away} setValue={setAway} disabled={disabled} testId={`quiz-away-score-${predId}`} />
    </div>
  );
}

export function QuizCard({ pred, onAnswered, bookmakers = [] }) {
  const [selected, setSelected] = useState(null);
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [matchAnswers, setMatchAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const type = pred.type || "marcatore";
  const answered = !!pred.user_answer;
  const expired = pred.expired;
  const resolved = pred.status === "resolved";
  const correctIds = pred.correct_option_ids || [];
  const isSchedina = type === "1x2" || type === "goalnogoal";
  const matches = pred.matches || [];
  const needsOption = type === "marcatore" || type === "combinato";
  const needsScore = type === "risultato" || type === "combinato";

  const allMatchesAnswered = !isSchedina || matches.every((m) => matchAnswers[m.id]);
  const canSubmit = (!needsOption || selected) && (!needsScore || (home !== "" && away !== "")) && allMatchesAnswered;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {};
      if (needsOption) payload.option_id = selected;
      if (needsScore) { payload.home_score = Number(home); payload.away_score = Number(away); }
      if (isSchedina) payload.match_answers = matchAnswers;
      await api.post(`/predictions/${pred.id}/answer`, payload);
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors: ["#22c55e", "#09090b", "#ffffff"] });
      toast.success("Pronostico inviato! La tua risposta è ora congelata.");
      onAnswered && onAnswered();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const shareTelegram = () => {
    const url = `${window.location.origin}/`;
    const text = `⚽ Pronostico Il Mago Tony: ${pred.question} — gioca con me!`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, "_blank");
  };

  const provaOra = (b) => {
    api.post(`/bookmakers/${b.id}/click`).catch(() => {});
    window.open(b.link, "_blank", "noopener");
  };

  const ua = pred.user_answer;

  return (
    <div data-testid={`quiz-card-${pred.id}`}
      className="tw-fade-up overflow-hidden rounded-3xl border-2 border-zinc-950 bg-white shadow-[6px_6px_0_0_#09090b]">
      {pred.image_path && (
        <div className="relative h-44 w-full overflow-hidden bg-zinc-950">
          <img src={fileUrl(pred.image_path)} alt="" className="h-full w-full object-cover" />
        </div>
      )}

      <div className="p-5">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-zinc-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-green-400">
            {TYPE_LABEL[type]}
          </span>
          <span className="rounded-full bg-green-500 px-2.5 py-1 text-xs font-black text-zinc-950">+{pred.points} PT</span>
          <button data-testid={`quiz-share-telegram-${pred.id}`} onClick={shareTelegram}
            className="ml-auto flex items-center gap-1 rounded-full bg-[#229ED9] px-3 py-1 text-xs font-bold text-white transition-transform hover:scale-105">
            <FaTelegram className="h-3.5 w-3.5" /> Condividi
          </button>
        </div>

        <h3 className="font-display text-xl font-extrabold uppercase leading-tight tracking-tight text-zinc-950">{pred.question}</h3>

        <div className="my-4 flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-zinc-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Scade tra</span>
          <Countdown deadline={pred.deadline} testId={`quiz-countdown-${pred.id}`} className="text-lg" />
        </div>

        {/* Score inputs for risultato/combinato */}
        {needsScore && (
          <div className="mb-3">
            {answered ? (
              <div className="flex items-center justify-center gap-3 rounded-2xl bg-slate-100 p-4">
                <span className="font-mono-t text-2xl font-black text-zinc-950">{ua.home_score} - {ua.away_score}</span>
                <span className="text-xs font-semibold uppercase text-zinc-500">il tuo risultato</span>
              </div>
            ) : (
              <ScoreInputs home={home} away={away} setHome={setHome} setAway={setAway} disabled={expired} predId={pred.id} />
            )}
            {resolved && (
              <p className="mt-2 text-center text-sm font-bold text-zinc-600">
                Risultato reale: <span className="font-mono-t text-green-600">{pred.correct_home_score} - {pred.correct_away_score}</span>
              </p>
            )}
          </div>
        )}

        {/* Options for marcatore/combinato */}
        {needsOption && (
          <div className="space-y-2">
            {pred.options.map((opt) => {
              const isChosen = answered && ua.option_id === opt.id;
              const isCorrect = resolved && correctIds.includes(opt.id);
              const isSelected = !answered && !expired && selected === opt.id;
              let cls = "border-slate-200 bg-white hover:border-zinc-950";
              if (isSelected) cls = "border-green-500 bg-green-50 ring-2 ring-green-500";
              if (isChosen && !resolved) cls = "border-zinc-950 bg-zinc-950/5";
              if (isCorrect) cls = "border-green-600 bg-green-50";
              if (isChosen && resolved && !isCorrect) cls = "border-red-500 bg-red-50";
              return (
                <button key={opt.id} data-testid={`quiz-option-${pred.id}-${opt.id}`}
                  disabled={answered || expired} onClick={() => setSelected(opt.id)}
                  className={`flex w-full items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all disabled:cursor-not-allowed ${cls}`}>
                  {opt.image_path && <img src={fileUrl(opt.image_path)} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />}
                  <span className="flex-1 font-semibold text-zinc-900">{opt.text}</span>
                  {isChosen && <Lock className="h-4 w-4 text-zinc-500" />}
                  {isCorrect && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                  {isChosen && resolved && !isCorrect && <XCircle className="h-5 w-5 text-red-500" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Schedina 1X2 / Goal-No Goal */}
        {isSchedina && (
          <div className="space-y-3" data-testid={`schedina-${pred.id}`}>
            {matches.map((m) => {
              const picked = answered ? (ua.match_answers || {})[m.id] : matchAnswers[m.id];
              return (
                <div key={m.id} className="rounded-2xl border border-slate-200 p-3">
                  {m.image_path && <img src={fileUrl(m.image_path)} alt="" className="mb-2 h-28 w-full rounded-xl object-cover" />}
                  <p className="mb-2 text-center text-sm font-bold text-zinc-900">{m.home_team} <span className="text-zinc-400">vs</span> {m.away_team}</p>
                  <div className={`grid gap-2 ${type === "1x2" ? "grid-cols-3" : "grid-cols-2"}`}>
                    {SCHEDINA_OPTS[type].map((o) => {
                      const isPicked = picked === o.v;
                      const isCorrect = resolved && m.correct_outcome === o.v;
                      const pickedWrong = resolved && isPicked && m.correct_outcome !== o.v;
                      let cls = "border-slate-200 bg-white text-zinc-700";
                      if (isPicked && !resolved) cls = "border-green-500 bg-green-50 text-zinc-950 ring-2 ring-green-500";
                      if (isCorrect) cls = "border-green-600 bg-green-50 text-green-700";
                      if (pickedWrong) cls = "border-red-500 bg-red-50 text-red-600";
                      return (
                        <button key={o.v} type="button" data-testid={`schedina-${pred.id}-${m.id}-${o.v}`}
                          disabled={answered || expired}
                          onClick={() => setMatchAnswers((s) => ({ ...s, [m.id]: o.v }))}
                          className={`rounded-xl border-2 py-2.5 text-sm font-black uppercase transition-all disabled:cursor-not-allowed ${cls}`}>
                          {o.l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer states */}
        <div className="mt-4">
          {!answered && !expired && (
            <Button data-testid={`quiz-submit-btn-${pred.id}`} onClick={submit} disabled={!canSubmit || submitting}
              className="w-full rounded-full bg-green-500 py-6 text-base font-black uppercase tracking-wide text-zinc-950 hover:bg-green-600 disabled:opacity-50">
              {submitting ? "Invio..." : "Conferma pronostico"}
            </Button>
          )}
          {answered && !resolved && (
            <div className="flex items-center justify-center gap-2 rounded-full bg-zinc-950 py-3 text-sm font-bold uppercase tracking-wide text-green-400">
              <Lock className="h-4 w-4" /> Risposta congelata
            </div>
          )}
          {answered && resolved && (
            <div className={`flex items-center justify-center gap-2 rounded-full py-3 text-sm font-black uppercase tracking-wide ${ua.is_correct ? "bg-green-500 text-zinc-950" : "bg-red-500 text-white"}`}>
              {ua.is_correct ? (<><Trophy className="h-4 w-4" /> Azzeccato! +{ua.points_awarded} punti</>) : (<><XCircle className="h-4 w-4" /> Pronostico errato</>)}
            </div>
          )}
          {!answered && expired && (
            <div className="flex items-center justify-center gap-2 rounded-full bg-slate-200 py-3 text-sm font-bold uppercase tracking-wide text-slate-500">
              <Clock className="h-4 w-4" /> Tempo scaduto
            </div>
          )}
        </div>

        {/* Mini comparatore bookmaker — SOLO dopo che l'utente ha risposto (congelato) */}
        {answered && bookmakers.length > 0 && (
          <div data-testid={`quiz-comparator-${pred.id}`} className="mt-5 rounded-2xl bg-zinc-950 p-4">
            <p className="text-center font-display text-lg font-black uppercase leading-tight text-white">
              Ora se vuoi davvero <span className="text-green-400">tentare la fortuna</span>
            </p>
            <div className="mt-3 space-y-2">
              {bookmakers.map((b) => (
                <div key={b.id} className="flex items-center gap-3 rounded-xl bg-white p-2.5">
                  {b.logo_path ? (
                    <img src={fileUrl(b.logo_path)} alt="" className="h-9 w-9 shrink-0 rounded-lg object-contain" />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-xs font-black text-green-400">{b.text?.slice(0, 2).toUpperCase()}</div>
                  )}
                  <span className="flex flex-1 items-center gap-1.5 truncate text-sm font-semibold text-zinc-900">
                    {b.is_top && <span className="shrink-0 rounded-full bg-yellow-400 px-1.5 py-0.5 text-[9px] font-black uppercase text-zinc-950">Top</span>}
                    <span className="truncate">{b.text}</span>
                  </span>
                  <button data-testid={`prova-ora-${b.id}`} onClick={() => provaOra(b)}
                    className="shrink-0 rounded-full bg-green-500 px-4 py-2 text-xs font-black uppercase tracking-wide text-zinc-950 transition-colors hover:bg-green-400">
                    Prova ora
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[11px] font-semibold text-zinc-400">Gioca responsabilmente · 18+</p>
          </div>
        )}

        {/* Social block under the answer options (compact round icons) */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <SocialLinks compact />
        </div>
      </div>
    </div>
  );
}
